import i18n from "i18next";
import React, { useEffect, useReducer, useState } from "react";
import { Manage } from "@/Pages/Manage/Manage";
import Authenticated from "@/Layouts/Authenticated";
import { Card } from "@/Components/Card";
import { usePage } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import { OrganizationLogoManagement } from "@/Components/Manage/OrganizationLogoManagement";
import MyModuleCard from "@/Components/Manage/MyModuleCard";
import styles from "@/../sass/components/AccountOverview.module.scss";
import "@/../sass/authenticated/adminaccountoverview.scss";
import ProfileInfoCard from "@/Pages/Manage/ProfileInfoCard";
import OrganizationInfoCard from "@/Pages/Manage/OrganizationInfoCard";
import SubscriptionsCard from "@/Pages/Manage/SubscriptionsCard";
import MiscHelpCards from "@/Pages/Manage/MiscHelpCards";
import SubscriptionCancellation from "@/Components/Modals/SubscriptionCancellation";
import SubscriptionCancellationConfirmation from "@/Components/Modals/SubscriptionCancellationConfirmation";
import { showChargebeeSection, showChargeBeeUi } from "@/Utilities/ChargeBee";
import FlashMessage from "@/Components/FlashMessage";

function reducer(state, action) {
    switch (action.type) {
        case "showCheckoutModal":
            return {
                ...state,
                showCheckoutModal: action.payload.showCheckoutModal,
            };
        case "editProfileModal":
            return {
                ...state,
                editProfileModal: action.payload.editProfileModal,
            };
        case "editOrgModal":
            return { ...state, editOrgModal: action.payload.editOrgModal };
        case "manageSubscriptionsModal":
            return {
                ...state,
                manageSubscriptionsModal:
                    action.payload.manageSubscriptionsModal,
            };
        case "cancelSubscriptionRequest":
            return {
                ...state,
                cancelSubscriptionRequest:
                    action.payload.cancelSubscriptionRequest,
            };
        case "cancelSubscriptionConfirmation":
            return {
                ...state,
                cancelSubscriptionConfirmation:
                    action.payload.cancelSubscriptionConfirmation,
            };
        default:
            return state;
    }
}

const AccountOverview = () => {
    const [chargebeeErrorMessage, setChargebeeErrorMessage] = useState(null);

    async function showChargebeeSubsciptions() {
        try {
            await showChargebeeSection(props, "PAYMENT_SOURCES");
        } catch (e) {
            setChargebeeErrorMessage(e.message);
        }
    }

    const cancellationConfirmation = usePage().props.confirmationModal;
    const cancellationToken = usePage().props.cancellationToken;
    const cancellationError = usePage().props.cancellation.error;
    const cancellationReasons = usePage().props.cancellation.reasons;

    let cancellationErrorExists = false;
    let cancellationTokenExists = false;

    if (cancellationError !== null) {
        cancellationErrorExists = true;
    }

    if (cancellationToken !== null) {
        cancellationTokenExists = true;
    }

    const [state, dispatch] = useReducer(reducer, {
        cancelSubscriptionRequest: {
            subscriptionType: null,
            dateOfRenewal: null,
            isOpen: false,
            setIsOpen: setCancelSubscriptionRequestOpen,
        },
        cancelSubscriptionConfirmation: {
            dateOfRenewal: null,
            isOpen:
                (cancellationConfirmation && cancellationTokenExists) ||
                cancellationErrorExists,
            cancellationErrorExists: cancellationErrorExists,
            cancellationError: cancellationError,
            cancellationToken: cancellationToken,
            cancellationReasons: cancellationReasons,
            setIsOpen: setCancelSubscriptionConfirmationOpen,
        },
        showCheckoutModal: false,
        editProfileModal: false,
        editOrgModal: false,
        manageSubscriptionsModal: false,
    });

    function setCancelSubscriptionRequestOpen(open) {
        dispatch({
            type: "cancelSubscriptionRequest",
            payload: {
                cancelSubscriptionRequest: {
                    subscriptionType: null,
                    dateOfRenewal: null,
                    isOpen: open,
                    setIsOpen: setCancelSubscriptionRequestOpen,
                },
            },
        });
    }

    function setCancelSubscriptionConfirmationOpen(open) {
        dispatch({
            type: "cancelSubscriptionConfirmation",
            payload: {
                cancelSubscriptionConfirmation: {
                    dateOfRenewal: null,
                    isOpen: open,
                    cancellationErrorExists: cancellationErrorExists,
                    cancellationError: cancellationError,
                    cancellationToken: cancellationToken,
                    cancellationReasons: cancellationReasons,
                    setIsOpen: setCancelSubscriptionConfirmationOpen,
                },
            },
        });
    }

    const isAdmin = usePage().props
        ? usePage().props.auth.current_organization?.is_admin
        : false;
    const props = usePage().props ? usePage().props : null;
    const subscriptionData = usePage().props.subscriptionData ?? [];
    let existingPayingCustomer =
        usePage().props.auth.current_organization.is_existing_paying_customer;
    const activeOrInTrialStatuses = props.activeOrInTrialStatuses;
    let activeOrInTrialModules = props.modules.active.filter((module) =>
        activeOrInTrialStatuses.includes(module.status)
    ); // trial or active
    let bundles = props.subscriptions;

    const openCancellationRequestModal = () => {
        dispatch({
            type: "cancelSubscriptionRequest",
            payload: {
                cancelSubscriptionRequest: {
                    isOpen: true,
                    setIsOpen: setCancelSubscriptionRequestOpen,
                },
            },
        });
    };

    return (
        <div>
            {isAdmin ? (
                <Manage
                    title={i18n.t("manage.account-overview")}
                    className="admin-account-overview"
                >
                    {showChargeBeeUi(props) && (
                        <div
                            style={{
                                justifyContent: "end",
                                display: "flex",
                            }}
                        >
                            <button
                                className="btn btn-primary"
                                onClick={showChargebeeSubsciptions}
                            >
                                Manage Payment Methods
                            </button>
                        </div>
                    )}

                    <div className="row">
                        <ProfileInfoCard
                            props={props}
                            state={state}
                            dispatch={dispatch}
                        />

                        <OrganizationInfoCard
                            props={props}
                            state={state}
                            dispatch={dispatch}
                            logoUrl={props.organization.logo_url}
                            onFileUploaded={(data) => {
                                Inertia.post(
                                    route("manage.organization.logo.store"),
                                    data
                                );
                            }}
                        />
                    </div>

                    <SubscriptionsCard
                        props={props}
                        state={state}
                        dispatch={dispatch}
                        openCancellationRequestModal={
                            openCancellationRequestModal
                        }
                        modules={activeOrInTrialModules}
                        dataTestid="account_overview_subscription"
                        bundles={bundles}
                    />

                    <MiscHelpCards
                        state={state}
                        dispatch={dispatch}
                        dataTestid="account_overview"
                    />
                </Manage>
            ) : (
                <Manage title={i18n.t("manage.account-overview")}>
                    <Card
                        dataTestid="account_overview_module"
                        title={i18n.t("organization-account.my-modules")}
                    >
                        <div className={styles.myModules}>
                            {usePage().props.modules.active.map(
                                (module, index) => (
                                    <MyModuleCard
                                        key={`active-module-${index}`}
                                        module={module}
                                        isActive={true}
                                        existingPayingCustomer={
                                            existingPayingCustomer
                                        }
                                    />
                                )
                            )}
                            {usePage().props.modules.inactive.map(
                                (module, index) => (
                                    <MyModuleCard
                                        key={`inactive-module-${index}`}
                                        module={module}
                                        isActive={false}
                                        existingPayingCustomer={
                                            existingPayingCustomer
                                        }
                                    />
                                )
                            )}
                            <a
                                className={styles.more}
                                href="https://www.ministrybrands.com/amplify/"
                                target="_blank"
                            >
                                {i18n.t("organization-account.more-ways")}{" "}
                                <i className="fa fa-external-link"></i>
                            </a>
                        </div>
                    </Card>
                    <Card
                        dataTestid="account_overview_organization_logo"
                        title={i18n.t("organization-account.logo")}
                    >
                        <OrganizationLogoManagement
                            organizationName={usePage().props.organization.name}
                            logoUrl={usePage().props.organization.logo_url}
                            onFileUploaded={(data) => {
                                Inertia.post(
                                    route("manage.organization.logo.store"),
                                    data
                                );
                            }}
                        ></OrganizationLogoManagement>
                    </Card>
                </Manage>
            )}
            {state.cancelSubscriptionRequest.isOpen && (
                <SubscriptionCancellation
                    subscriptionType={subscriptionData.subscriptionType}
                    dateOfRenewal={subscriptionData.renewalDate}
                    isOpen={state.cancelSubscriptionRequest.isOpen}
                    setIsOpen={state.cancelSubscriptionRequest.setIsOpen}
                ></SubscriptionCancellation>
            )}
            {state.cancelSubscriptionConfirmation.isOpen && (
                <SubscriptionCancellationConfirmation
                    dateOfRenewal={subscriptionData.renewalDate}
                    isOpen={state.cancelSubscriptionConfirmation.isOpen}
                    cancellationInvalid={
                        state.cancelSubscriptionConfirmation.cancellationInvalid
                    }
                    cancellationErrorExists={
                        state.cancelSubscriptionConfirmation
                            .cancellationErrorExists
                    }
                    cancellationError={
                        state.cancelSubscriptionConfirmation.cancellationError
                    }
                    cancellationToken={
                        state.cancelSubscriptionConfirmation.cancellationToken
                    }
                    cancellationReasons={
                        state.cancelSubscriptionConfirmation.cancellationReasons
                    }
                    setIsOpen={state.cancelSubscriptionConfirmation.setIsOpen}
                ></SubscriptionCancellationConfirmation>
            )}

            {chargebeeErrorMessage && (
                <FlashMessage
                    message={chargebeeErrorMessage}
                    type="danger"
                    hasClosed={() => setChargebeeErrorMessage(null)}
                />
            )}
        </div>
    );
};

AccountOverview.layout = (page) => (
    <Authenticated children={page}></Authenticated>
);
export default AccountOverview;
